import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { POSTS } from "@/content/posts";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "SmartEssay Blog — Essay Writing Guides for Students" },
      { name: "description", content: "Teacher-built guides on essay outlines, citations, AI honestly, and the small writing habits that move grades. From SmartEssay and Tilt University." },
      { property: "og:title", content: "SmartEssay Blog — Essay Writing Guides for Students" },
      { property: "og:description", content: "Teacher-built guides on essay outlines, citations, and AI honestly." },
      { property: "og:url", content: "https://smartessay.ai/blog" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/blog" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "SmartEssay Blog",
        url: "https://smartessay.ai/blog",
        publisher: { "@type": "Organization", name: "Tilt University", url: "https://tilt.university" },
        blogPost: POSTS.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          url: `https://smartessay.ai/blog/${p.slug}`,
          datePublished: p.publishedAt,
          author: { "@type": "Organization", name: p.author },
        })),
      }),
    }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-6 pb-24">
        <header className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">SmartEssay blog</p>
          <h1 className="mt-3 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            Essay guides that don't <em>cheat</em> you out of learning
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Practical writing guides from a 27-year English teacher. No filler, no AI sludge — just the rules, examples, and habits that actually move grades.
          </p>
        </header>

        <ul className="mt-14 grid gap-6 sm:grid-cols-2">
          {POSTS.map((p) => (
            <li key={p.slug}>
              <Link
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:border-primary/40 hover:shadow-glow"
              >
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(p.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {p.readingMinutes} min read
                  </span>
                </div>
                <h2 className="mt-3 font-display text-xl font-semibold leading-snug tracking-tight text-foreground group-hover:text-primary">
                  {p.title}
                </h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{p.description}</p>
                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                  Read guide
                  <ArrowRight className="ml-1.5 h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
